import React from 'react';
import { StyleSheet, View } from 'react-native';

import { UserAvatar } from '@components/Avatar';
import Badge from '@components/Badge';
import IconButton from '@components/IconButton';
import { PressableOpacity } from '@components/Pressable';
import { useChats } from '@context/ChatsContext';
import { useNotifications } from '@context/NotificationsContext';
import { useCurrentUser } from '@context/UsersContext';
import type { AppNavigation } from '@navigation/AppNavigation';

const BADGE_SIZE = 15;
const HEADER_AVATAR_SIZE = 32;

type NavigationProp = {
  setOptions: (options: {
    headerLeft?: () => React.ReactNode;
    headerRight?: () => React.ReactNode;
  }) => void;
};

export type HeaderProps = {
  appNavigation: Pick<
    AppNavigation,
    'openChatThreads' | 'openCurrentUserProfile' | 'openNotifications'
  >;
  navigation: NavigationProp;
};

export function useHeader({ appNavigation, navigation }: HeaderProps) {
  useHeaderLeft({ appNavigation, navigation });
  useHeaderRight({ appNavigation, navigation });
}

export function useHeaderLeft({ appNavigation, navigation }: HeaderProps) {
  const { userDoc } = useCurrentUser();

  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerLeft: () =>
        userDoc ? (
          <PressableOpacity
            onPress={appNavigation.openCurrentUserProfile}
            style={styles.profileButton}
            testID="navigation.Header.ProfileButton">
            <UserAvatar
              displayName={userDoc.displayName}
              photoUrl={userDoc.photoUrl}
              size={HEADER_AVATAR_SIZE}
            />
          </PressableOpacity>
        ) : null,
    });
  }, [appNavigation.openCurrentUserProfile, navigation, userDoc]);
}

export function useHeaderRight({ appNavigation, navigation }: HeaderProps) {
  const { unreadMessageCount } = useChats();
  const { unreadNotificationCount } = useNotifications();

  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <View style={styles.rightContainer}>
          <View>
            <Badge visible={unreadMessageCount > 0} style={styles.badge} size={BADGE_SIZE}>
              {unreadMessageCount}
            </Badge>
            <IconButton
              icon="message-text-outline"
              onPress={appNavigation.openChatThreads}
              testID="navigation.Header.ChatIconButton"
            />
          </View>
          <View>
            <Badge visible={unreadNotificationCount > 0} style={styles.badge} size={BADGE_SIZE}>
              {unreadNotificationCount}
            </Badge>
            <IconButton
              icon="bell-outline"
              onPress={appNavigation.openNotifications}
              testID="navigation.Header.NotificationsIconButton"
            />
          </View>
        </View>
      ),
    });
  }, [
    appNavigation.openChatThreads,
    appNavigation.openNotifications,
    navigation,
    unreadMessageCount,
    unreadNotificationCount,
  ]);
}

const styles = StyleSheet.create({
  badge: {
    backgroundColor: 'red',
    position: 'absolute',
    right: 5,
    top: 5,
    zIndex: 1,
  },
  profileButton: {
    paddingHorizontal: 10,
  },
  rightContainer: {
    flexDirection: 'row',
  },
});
